import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { RouteStore } from "src/app/shared/stores/route-store";
import { CaseContextData } from "../../store-management/cases/case.resolver";
import {
  CaseListFilter,
  CaseReimbursementFilterValue,
  CaseRouteFilterForm,
  DEFAULT_CASE_LIST_PAGE_SIZE,
} from "../../store-management/shared/case-list-query.builder";
import { ClaimContextData } from "../../store-management/claims/claim.resolver";
import {
  ClaimListFilter,
  DEFAULT_CLAIM_LIST_PAGE_SIZE,
} from "../../store-management/shared/claim-list-query.builder";
import { DashboardListNavigationContext } from "../models/dashboard-list-navigation.model";
import {
  resolveClaimTypeListNavigationTarget,
} from "./dashboard-claim-type-navigation.helper";
import {
  CLAIM_TYPE_VIEW_ROUTE_NAVIGATION_STATE_KEY,
  RouteViewStateData,
} from "../../store-management/shared/route-view-state.helper";
import {
  DEFAULT_REPORT_PAGE_SIZE,
} from "../../reports/shared/report-grid-page-size.constants";

export const CASE_ROUTE_NAVIGATION_STATE_KEY = "caseRouteContext";
export const CLAIM_ROUTE_NAVIGATION_STATE_KEY = "claimRouteContext";
export const REIMBURSEMENTS_REPORT_ROUTE_NAVIGATION_STATE_KEY =
  "reimbursementsReportRouteContext";
export { CLAIM_TYPE_VIEW_ROUTE_NAVIGATION_STATE_KEY };

@Injectable({ providedIn: "root" })
export class DashboardNavigationService {
  private readonly router = inject(Router);
  private readonly routeStore = inject(RouteStore);

  navigateToClaimsList(
    context: DashboardListNavigationContext,
    claimType = "",
    statusValue = "",
  ): void {
    const filterForm: ClaimListFilter = {
      provider: context.provider ?? "",
      storeId: context.storeId ?? "",
      claimType,
      status: statusValue,
      startFiledDate: context.startDate ?? "",
      endFiledDate: context.endDate ?? "",
    };

    const routeContext: ClaimContextData = {
      filterForm,
      paginatorState: {
        pageIndex: 0,
        pageSize: DEFAULT_CLAIM_LIST_PAGE_SIZE,
        cursors: [null],
      },
    };

    this.routeStore.setContextDataForRoute("store/claims", routeContext, {
      breadcrumb: "Claim Management",
      initialPath: "store/claims",
      mode: "browser",
    });
    this.router.navigate(["/store/claims"], {
      state: { [CLAIM_ROUTE_NAVIGATION_STATE_KEY]: routeContext },
      onSameUrlNavigation: "reload",
    });
  }

  /** Navigates to a claim-type list view with the dashboard store filter (no date range). */
  navigateToClaimTypeView(
    context: DashboardListNavigationContext,
    claimType: string,
  ): void {
    const target = resolveClaimTypeListNavigationTarget(claimType);
    if (!target) {
      this.navigateToClaimsList(context, claimType);
      return;
    }

    const routeContext: RouteViewStateData = {
      filterForm: {
        storeId: context.storeId ?? "",
      },
      paginatorState: {
        pageIndex: 0,
        pageSize: DEFAULT_CLAIM_LIST_PAGE_SIZE,
        cursors: [null],
      },
    };

    this.routeStore.setContextDataForRoute(target.routeKey, routeContext, {
      breadcrumb: target.breadcrumb,
      initialPath: target.routeKey,
      mode: "browser",
    });

    this.router.navigate([target.navigatePath], {
      state: {
        [CLAIM_TYPE_VIEW_ROUTE_NAVIGATION_STATE_KEY]: routeContext,
      },
      onSameUrlNavigation: "reload",
    });
  }

  navigateToCasesList(
    context: DashboardListNavigationContext,
    statusValue = "",
    reimbursementFilter: CaseReimbursementFilterValue = "all",
  ): void {
    const filterForm: CaseRouteFilterForm = {
      caseId: "",
      provider: context.provider ?? "",
      storeId: context.storeId ?? "",
      claimType: "",
      status: statusValue,
      startFiledDate: context.startDate ?? "",
      endFiledDate: context.endDate ?? "",
      reimbursementFilter,
    };

    const routeContext: CaseContextData = {
      filterForm,
      paginatorState: {
        pageIndex: 0,
        pageSize: DEFAULT_CASE_LIST_PAGE_SIZE,
        cursors: [null],
      },
    };

    this.routeStore.setContextDataForRoute("store/cases", routeContext, {
      breadcrumb: "Case Management",
      initialPath: "store/cases",
      mode: "browser",
    });
    this.router.navigate(["/store/cases"], {
      state: { [CASE_ROUTE_NAVIGATION_STATE_KEY]: routeContext },
      onSameUrlNavigation: "reload",
    });
  }

  navigateToReimbursementsReport(
    context: DashboardListNavigationContext,
  ): void {
    const filterForm = {
      caseId: "",
      provider: context.provider ?? "",
      storeId: context.storeId ?? "",
      startFiledDate: context.startDate ?? "",
      endFiledDate: context.endDate ?? "",
      status: "",
      claimType: "",
      minReimbursementAmount: "",
      maxReimbursementAmount: "",
    };

    const routeContext: RouteViewStateData = {
      filterForm,
      paginatorState: {
        pageIndex: 0,
        pageSize: DEFAULT_REPORT_PAGE_SIZE,
        cursors: [null],
      },
    };

    this.routeStore.setContextDataForRoute(
      "report/reimbursements",
      routeContext,
      {
        breadcrumb: "Reimbursements Report",
        initialPath: "report/reimbursements",
        mode: "browser",
      },
    );
    this.router.navigate(["/report/reimbursements"], {
      state: {
        [REIMBURSEMENTS_REPORT_ROUTE_NAVIGATION_STATE_KEY]: routeContext,
      },
      onSameUrlNavigation: "reload",
    });
  }
}
